/* ════════════════════════════════════════════════════════════════
   CULTURE 43.2 FM — PLAYLIST SYSTEM (V2 FULL REBUILD)

   CHECKPOINT 20 FIX — RACE CONDITION:
   loadPlaylist()/loadMixes() are async (they fetch playlist.json /
   mixes.json over the network). lf-fm-player.js used to read
   window.LF_PLAYLIST synchronously the instant DOMContentLoaded
   fired — before these fetches had any chance to resolve. That's
   why bulk-uploaded songs weren't showing: whatever had (or hadn't)
   landed in window.LF_PLAYLIST at that exact instant got frozen
   into the player's state permanently, for the rest of the page's
   life. Reloading didn't reliably fix it either, since it's a timing
   race, not a data problem.

   window.LF_FM_READY (bottom of this file) is the fix: a single
   promise that resolves only once BOTH playlist.json and mixes.json
   have been fetched AND parsed. lf-fm-player.js now awaits it before
   ever touching window.LF_PLAYLIST / window.LF_MIXES. Nothing about
   the JSON format, the fetch logic, or the track-building logic
   below has changed.
═══════════════════════════════════════════════════════════════ */

/* ─────────────────────────────────────────────
   TRACK FACTORY
───────────────────────────────────────────── */
function TRACK(title, artist, src, type = "track") {
  return { title, artist, src, type };
}

/* ─────────────────────────────────────────────
   TITLE CLEANUP HELPERS
   Filenames are never edited or removed — only the
   DISPLAYED title is cleaned up, at load time.
───────────────────────────────────────────── */
const LF_UUID_PATTERN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

/* Strip a leading track-index number ("05 ", "10_") — but leave real
   numeric titles like "10.325" alone (no space/underscore right after). */
function _stripNumberPrefix(str) {
  return str.replace(/^\d{1,3}[\s_]+/, "");
}

/* Cut everything from a standalone "prod" marker onward — covers
   "prod", "prod.", "prod by", "produced by" plus whatever junk
   (producer/engineer names) follows it. */
function _stripProducerTag(str) {
  const words = str.split(/[_\s]+/);
  const idx = words.findIndex(w => /^prod(uced)?\.?$/i.test(w));
  return idx === -1 ? str : words.slice(0, idx).join("_");
}

/* Drop auto-generated UUID/hex junk (duplicate-upload suffixes) —
   the track stays, only the meaningless suffix is removed. */
function _stripUuid(str) {
  return str.replace(LF_UUID_PATTERN, "").trim();
}

/* Restore apostrophes lost when the filename was sanitized. */
function _fixContractions(str) {
  return str
    .replace(/\b(don|won|can|isn|wasn|aren|weren|wouldn|couldn|shouldn|didn|doesn|hasn|haven|hadn|ain)[\s_]+t\b/gi, "$1't")
    .replace(/\b(what|that|it|he|she|there|here|let|who|i)[\s_]+(s|m|ll|d|ve)\b/gi, "$1'$2")
    .replace(/\b(you|we|they)[\s_]+(re|ll|d|ve)\b/gi, "$1'$2");
}

/* Category/artist name must never appear inside its own song title. */
function _removeArtistFromTitle(str, artist) {
  if (!artist) return str;
  const esc = artist.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return str.replace(new RegExp("\\b" + esc + "\\b", "gi"), "").replace(/\s+/g, " ").trim();
}

/* CHECKPOINT 27 (PLAYLIST CLEANUP — "tell the difference between the
   title and the artist name"): filenames on this catalog commonly
   stack THREE kinds of name onto one string with no consistent
   punctuation — a real artist mention (may or may not be the known
   artist for that folder), a filler/uploader/leak tag that looks
   exactly like a name but isn't one ("fentanvl", "User_906753258"),
   and a trailing producer/engineer tag. A plain regex can't tell
   these apart from spelling alone — the only reliable signal is
   POSITION relative to a name we already know is real: the artist
   key from playlist.json. window.LF_KNOWN_ARTISTS is populated (see
   loadPlaylist() below) before any filename is parsed, so the
   segment-splitter below can anchor on "where does the real artist
   sit in this filename" and treat everything else around it as
   noise instead of guessing from word content. */
window.LF_KNOWN_ARTISTS = [];

const LF_SEGMENT_SPLIT = /[\s_]-[\s_]/;

function _normName(s) {
  return String(s || "").replace(/[_\s]+/g, " ").trim().toLowerCase();
}

/* Does this segment (a whole dash-delimited chunk of the filename)
   represent the known artist? Exact match handles the common case
   ("Nine_Vicious"); startsWith handles a segment where a second,
   real featured artist got glued onto the same segment with no
   separator ("1300SAINT_Nine_Vicious" — still "starts with" the
   known artist). */
function _matchKnownArtist(segment, artist) {
  const seg = _normName(segment);
  const a = _normName(artist);
  if (!seg || !a) return false;
  return seg === a || seg.indexOf(a) === 0;
}

/* Splits the raw (pre-extension, pre-number-prefix) filename on
   "_-_" / " - " style separators, finds which segment (if any) is
   the real known artist, and returns just the segment(s) that are
   actually the title — dropping everything that's positioned as a
   leading/trailing name-tag around it. See file header for why
   position (not word content) is the signal used here.

   - No dash structure at all -> nothing to split, whole string is
     the title candidate (unchanged from the old behavior).
   - Artist found as the FIRST segment -> title is everything after
     it (standard "Artist_-_Title" filenames).
   - Artist found as the LAST segment -> title is just the ONE
     segment immediately before it (standard "Tag_-_Title_-_Artist"
     reversed-order filenames — any segments further back are extra
     leading noise tags, dropped).
   - Artist found in the MIDDLE -> title is everything after it
     (handles "Noise_-_Artist_-_Title_producer" — the single most
     common shape on this catalog).
   - Artist not found anywhere -> best-effort fallback: drop just the
     first segment (on this catalog that's always a filler/leak/
     engineer tag, never part of a real title) and keep the rest. */
function _splitFillerSegments(raw, artist) {
  if (!LF_SEGMENT_SPLIT.test(raw)) return raw;

  const segments = raw.split(LF_SEGMENT_SPLIT);
  if (segments.length < 2) return raw;

  let artistIdx = -1;
  if (artist) {
    artistIdx = segments.findIndex(seg => _matchKnownArtist(seg, artist));
  }
  /* Fall back to checking against the FULL known-artist roster, not
     just this track's own folder — covers a real featured artist's
     name sitting where the filler tag usually goes. */
  if (artistIdx === -1 && window.LF_KNOWN_ARTISTS.length) {
    artistIdx = segments.findIndex(seg =>
      window.LF_KNOWN_ARTISTS.some(a => _matchKnownArtist(seg, a))
    );
  }

  if (artistIdx === -1) {
    // No known-artist anchor found — best-effort: the first segment
    // on this catalog is always noise (uploader ID, leak tag, radio
    // handle), never real title text.
    return segments.slice(1).join(" ");
  }
  if (artistIdx === 0) return segments.slice(1).join(" ");
  if (artistIdx === segments.length - 1) return segments[artistIdx - 1];
  return segments.slice(artistIdx + 1).join(" ");
}

/* CHECKPOINT 27 (PLAYLIST CLEANUP — "Remove random producer names" /
   "the name filtering is a little bugged"): a handful of real
   filenames append a bare producer/engineer name with no "prod"/
   "produced by" marker for _stripProducerTag to catch (e.g.
   "..._Face_V2_ahmad.mp3"). These are real, known tags on this
   catalog — stripped by name, conservatively, only when TRAILING
   (popped one at a time off the end, so a stack of several tags on
   one file all get removed, but a real title word is never eaten).
   This list is meant to stay short and specific — add a name here
   the moment it shows up wrong, rather than trying to guess every
   possible producer tag in advance. */
const LF_KNOWN_PRODUCER_TAGS = [
  "ahmad", "ahamad", "bella", "patrick", "benjicold", "jwade", "pyrex",
  "ninevicious", "nosaint", "clayco", "perk", "n9ck", "r8", "og",
  "georgewho", "elliott", "saint"
];
/* Extension typos baked directly into the filename text itself
   ("..._mp3.mp3", "..._wav.m4a") — not part of anyone's name, just
   leftover format-conversion junk. Stripped the same trailing-word
   way, in the same pass, so a stack like "..._ahmad_mp3" pops both
   in one go regardless of order. */
const LF_EXTENSION_JUNK = ["mp3", "wav", "m4a", "wav2"];
const LF_TRAILING_STRIP = LF_KNOWN_PRODUCER_TAGS.concat(LF_EXTENSION_JUNK);

function _stripKnownProducerNames(str) {
  const words = str.split(/\s+/);
  while (words.length > 1 && LF_TRAILING_STRIP.includes(words[words.length - 1].toLowerCase())) {
    words.pop();
  }
  return words.join(" ").trim();
}

/* CHECKPOINT 23 (PLAYLIST CLEANUP — "Keep: V1 v2 v3. Lowercase v."):
   normalizes version markers to a consistent lowercase "v1"/"v2" style
   regardless of how the filename capitalized it, without touching any
   other digit in the title (word-boundary + single/double digit only,
   so it can't misfire on something like "MERCEDES. 2005"). */
function _normalizeVersionLabel(str) {
  return str.replace(/\bV(\d{1,2})\b/g, "v$1");
}

/* ─────────────────────────────────────────────
   AUTO TITLE PARSER
───────────────────────────────────────────── */
function buildTitleFromFilename(url, artist = "") {
  const file = url.split('/').pop().split('?')[0];
  let raw = file.replace(/\.[^/.]+$/, "");

  raw = _stripNumberPrefix(raw);
  raw = _splitFillerSegments(raw, artist);   // peel off leading/trailing name-tags around the real artist
  raw = _stripProducerTag(raw);              // cut everything from an explicit "prod" marker onward

  const featRe = /[\s_]+(feat\.?|ft\.?|featuring|with|w\.)[\s_]+/i;
  const m = raw.match(featRe);

  let title = raw;
  let feat = "";
  let featLabel = "feat";

  if (m) {
    title = raw.slice(0, m.index);
    feat  = raw.slice(m.index + m[0].length);
    featLabel = /with|w\./i.test(m[1]) ? "with" : "feat";
  } else if (artist && window.LF_KNOWN_ARTISTS.length) {
    /* CHECKPOINT 27 — "if you ever see another artist name in the
       title add it as a feat or a with, and put the parenthesis":
       no explicit ft/feat/with marker, but a DIFFERENT known artist
       (not this track's own artist) shows up as a trailing segment
       or trailing run of words — pull it out as a "with" credit
       instead of leaving it jammed into the title text. Conservative
       on purpose: only ever looks at the tail end of the string, and
       only ever matches an artist from the real roster, never a
       guess at an unknown name. */
    const words = raw.split(/[\s_]+/);
    for (let take = Math.min(4, words.length - 1); take >= 1; take--) {
      const tail = words.slice(words.length - take).join(" ");
      const isOther = window.LF_KNOWN_ARTISTS.some(a =>
        _normName(a) !== _normName(artist) && _normName(tail) === _normName(a)
      );
      if (isOther) {
        title = words.slice(0, words.length - take).join(" ");
        feat = tail;
        featLabel = "with";
        break;
      }
    }
  }

  const clean = (str) =>
    _stripUuid(str)
      .replace(/[_-]+/g, " ")
      .replace(/[^a-zA-Z0-9.'\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  title = clean(title);
  feat  = clean(feat);

  title = _removeArtistFromTitle(title, artist);
  feat  = _removeArtistFromTitle(feat, artist);

  title = _stripKnownProducerNames(title);
  feat  = _stripKnownProducerNames(feat);

  title = _normalizeVersionLabel(title);
  feat  = _normalizeVersionLabel(feat);

  title = _fixContractions(title);
  feat  = _fixContractions(feat);

  if (feat && artist && feat.toLowerCase() === artist.toLowerCase()) feat = "";
  if (!title) title = clean(raw) || "Untitled"; // never surface a blank title if stripping went too far

  /* CHECKPOINT 23 (PLAYLIST CLEANUP — "Handle: feat. ft. with w.
     properly."): only "feat" is a genuine abbreviation that takes a
     period ("feat."). "with" is a whole word, not an abbreviation, so
     appending "." after it read as a typo ("Song (with. Artist)").
     Fixed to only punctuate when the label actually is "feat". */
  return feat
    ? `${title} (${featLabel}${featLabel === "feat" ? "." : ""} ${feat})`
    : title;
}

/* ─────────────────────────────────────────────
   METADATA OVERRIDE SYSTEM (checkpoint 23)
   Purpose: rename a song's displayed title/artist WITHOUT touching
   the actual filename — for the rare track whose auto-parsed name
   (buildTitleFromFilename above) still isn't right and isn't worth
   teaching the general parser a one-off rule for.

   Deliberately kept completely separate from playlist generation:
   this is just a flat filename → override lookup table, read from
   its own JSON file (assets/lf-playlist-overrides.json). Nothing in
   this block knows how titles get parsed, and nothing in the parser
   above knows this override table exists — loadPlaylist()/loadMixes()
   are the only two places that consult it, as a single optional
   "does an override exist for this file? use it, else keep the
   computed value" check per track. See PLAYLIST-OVERRIDES-README.md
   at the theme root for the exact JSON shape and how to add an entry.
───────────────────────────────────────────── */
window.LF_OVERRIDES = {};

function _overrideKeyFor(url) {
  return decodeURIComponent(String(url || "").split('/').pop().split('?')[0]);
}

async function loadOverrides() {
  if (!window.LF_OVERRIDES_URL) { window.LF_OVERRIDES = {}; return; }
  try {
    const res = await fetch(window.LF_OVERRIDES_URL);
    const data = await res.json();
    window.LF_OVERRIDES = (data && typeof data === "object" && data.overrides) ? data.overrides : {};
  } catch (e) {
    // Missing/empty overrides file is a normal, expected state (nothing
    // to override yet) — not an error worth alarming anyone over.
    window.LF_OVERRIDES = {};
  }
}

/* Returns { title, artist } — the override's values where provided,
   falling back field-by-field to whatever was already computed. */
function _applyOverride(url, computedTitle, computedArtist) {
  const entry = window.LF_OVERRIDES[_overrideKeyFor(url)];
  if (!entry) return { title: computedTitle, artist: computedArtist };
  return {
    title: entry.title || computedTitle,
    artist: entry.artist || computedArtist
  };
}

/* ─────────────────────────────────────────────
   STATE
───────────────────────────────────────────── */
window.LF_PLAYLIST = [];
window.LF_MIXES = [];

/* ─────────────────────────────────────────────
   MODE SYSTEM
───────────────────────────────────────────── */
window.LF_FM_MODE = "playlist";

window.LF_FM_STATE = {
  currentIndex: 0,
  isPlaying: false,
  shuffle: false
};

/* ─────────────────────────────────────────────
   SETTINGS
───────────────────────────────────────────── */
window.LF_FM_SETTINGS = {
  defaultShuffle: true,
  autoplay: false,
  showQueue: true,
  crossfadeSecs: 2,
  stationName: "CULTURE 43.2 FM",
  stationTagline: "The Culture. Live.",
  mode: "playlist"
};

/* ─────────────────────────────────────────────
   LOADER — playlist.json
───────────────────────────────────────────── */
async function loadPlaylist() {
  try {
    const res = await fetch(window.LF_PLAYLIST_URL);
    const data = await res.json();

    const artists = data.artists || data || {};

    // Populate the known-artist roster BEFORE parsing any filename —
    // see file header + _splitFillerSegments: the parser needs the
    // full artist list as an anchor to tell a real artist mention
    // apart from a filler/uploader tag sitting in the same spot.
    window.LF_KNOWN_ARTISTS = Object.keys(artists);

    // FIX: avoid wiping global state mid-session
    const newPlaylist = [];

    Object.keys(artists).forEach(artist => {
      artists[artist].forEach(url => {
        const computedTitle = buildTitleFromFilename(url, artist);
        const final = _applyOverride(url, computedTitle, artist);
        newPlaylist.push(TRACK(final.title, final.artist, url, "track"));
      });
    });

    window.LF_PLAYLIST = newPlaylist;

  } catch (e) {
    console.error("PLAYLIST LOAD FAILED", e);
    window.LF_PLAYLIST = [];
  }
}

/* ─────────────────────────────────────────────
   LOADER — mixes.json
───────────────────────────────────────────── */
async function loadMixes() {
  try {
    const res = await fetch(window.LF_MIXES_URL);
    const data = await res.json();

    window.LF_MIXES = (data.mixes || [])
      .filter(m => m && m.url && m.url.length > 0)
      .map(m => {
        const computedTitle = m.title || buildTitleFromFilename(m.url, m.artist || "");
        const final = _applyOverride(m.url, computedTitle, m.artist || "");
        return TRACK(final.title, final.artist, m.url, "mix");
      });

  } catch (e) {
    console.error("MIXES LOAD FAILED", e);
    window.LF_MIXES = [];
  }
}

/* ─────────────────────────────────────────────
   QUEUE ENGINE
───────────────────────────────────────────── */
window.getActiveQueue = function () {
  return window.LF_FM_MODE === "mix"
    ? window.LF_MIXES
    : window.LF_PLAYLIST;
};

/* ─────────────────────────────────────────────
   SHUFFLE ENGINE
───────────────────────────────────────────── */
window.getProcessedQueue = function () {
  let queue = [...window.getActiveQueue()];

  if (
    window.LF_FM_MODE === "playlist" &&
    window.LF_FM_SETTINGS.defaultShuffle
  ) {
    queue.sort(() => Math.random() - 0.5);
  }

  return queue;
};

/* ─────────────────────────────────────────────
   MODE SWITCHER
───────────────────────────────────────────── */
window.setFMMode = function(mode) {
  if (mode !== "playlist" && mode !== "mix") return;

  window.LF_FM_MODE = mode;
  window.LF_FM_STATE.currentIndex = 0;
};

/* ─────────────────────────────────────────────
   INIT — exposes window.LF_FM_READY
   Any script that needs window.LF_PLAYLIST / window.LF_MIXES to be
   fully and correctly loaded MUST await this before reading them.
───────────────────────────────────────────── */
window.LF_FM_READY = (async function initFMSystem() {
  await loadOverrides();
  await loadPlaylist();
  await loadMixes();

  return true;
})();